import { AnalyticsCategoryNavigation } from "@components/analytics/in-house/analytics-category-navigation";
import { AnalyticsChart } from "@components/analytics/in-house/analytics-chart";
import { AnalyticsSearchFilter } from "@components/analytics/in-house/analytics-search-filter";
import { AnalyticsTable } from "@components/analytics/in-house/analytics-table";
import { IN_HOUSE_ANALYTICS_SECTION_GAP } from "@components/analytics/in-house/in-house-analytics-styles";
import { DateRangeValue } from "@components/date-range-picker/date-range-picker-types";
import { formatRangeLengthInDays } from "@components/date-range-picker/date-range-picker-utils";
import { Stack } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { AnalyticsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

// Range of 30 days is the default for the analytics page, so we use it to initialize the selected range.
const RANGE_DAYS_30 = 30;

/**
 * Composes the in-house analytics page sections and owns shared range state.
 * The selected range is passed to the search/filter controls and chart.
 */
export function InHouseAnalyticsPage(): JSX.Element {
  const { trackEvent } = useTrackEvent();

  const [selectedRange, setSelectedRange] = useState<DateRangeValue>(() => {
    const today = DateTime.now();
    return [
      today
        .minus({ days: RANGE_DAYS_30 - 1 })
        .startOf("day")
        .toJSDate(),
      today.endOf("day").toJSDate(),
    ];
  });

  const handleSelectedRangeChange = useCallback(
    (range: DateRangeValue) => {
      setSelectedRange(range);

      const timeFrameLength = formatRangeLengthInDays(range);
      if (!timeFrameLength) {
        return;
      }

      trackEvent({
        name: AnalyticsEvents.changeDateRange,
        props: { timeFrameLength },
      });
    },
    [trackEvent]
  );

  return (
    <Stack
      sx={{
        width: "100%",
        gap: IN_HOUSE_ANALYTICS_SECTION_GAP,
        py: IN_HOUSE_ANALYTICS_SECTION_GAP,
      }}
    >
      {/* Top bar: Search/Filter + Date Selector */}
      <Stack
        direction="row"
        sx={{ gap: IN_HOUSE_ANALYTICS_SECTION_GAP, flexWrap: "wrap" }}
      >
        <AnalyticsSearchFilter
          value={selectedRange}
          onChange={handleSelectedRangeChange}
        />
      </Stack>

      {/* Category navigation tiles */}
      <AnalyticsCategoryNavigation />

      {/* Chart area */}
      <AnalyticsChart selectedRange={selectedRange} />

      {/* Data table */}
      <AnalyticsTable />
    </Stack>
  );
}
